/**
 * Zusammengeführte Legende des Grundkalenders (Board „Vertrieb“): Balkenfarben, Sperre, Schloss,
 * Standardpreis, Kanalregel, Mindestnächte und Auswahl. Den Wohnungsverbund erklärt sie nur, wenn
 * er im geladenen Zeitraum eine Nacht schließt oder doppelt belegt.
 */
import type { ReactNode } from 'react';
import { Link2, Lock } from 'lucide-react';
import type {
  AvailabilityDistributionApartment,
  AvailabilityDistributionChannelCloseout,
} from '@/shared/api';
import { OtaLogo } from '@/features/properties/OtaLogo';
import {
  DOUBLE_BOOKED_LABEL,
  DoubleBookedMark,
} from '@/features/properties/flat-family/DoubleBookedMark';
import { useAutomationLayer } from '../automation/AutomationLayerContext';
import { ModeChip } from '../automation/AutomationMarks';
import { FAMILY_CLOSED_LABEL } from './DayCellBody';
import { familyMarks } from './calendarFamilyMarks';

const BARS: [string, string][] = [
  ['bg-navy', 'Booking.com'],
  ['bg-sealwax', 'Airbnb'],
  ['bg-amber', 'Manuelle Buchung'],
  ['bg-ink-soft', 'Andere Buchung'],
];

export function CalendarLegend({
  apartments = [],
  closeouts = [],
}: {
  apartments?: readonly AvailabilityDistributionApartment[];
  closeouts?: readonly AvailabilityDistributionChannelCloseout[];
}) {
  const { closedByFamily, doubleBooked } = familyMarks(apartments, closeouts);
  const automation = useAutomationLayer() !== null;
  return (
    <div
      className="text-ink-soft flex flex-wrap items-center gap-x-4 gap-y-2 px-1 text-xs"
      aria-label="Kalenderlegende"
    >
      {BARS.map(([color, label]) => (
        <Item key={label} swatch={<span className={`h-3 w-6 rounded ${color}`} />} label={label} />
      ))}
      <Item
        swatch={
          <span className="h-4 w-6 rounded bg-[repeating-linear-gradient(135deg,var(--color-sand)_0_4px,var(--color-paper)_4px_8px)]" />
        }
        label="gesperrt"
      />
      <Item
        swatch={<Lock size={11} strokeWidth={2.6} aria-hidden />}
        label="von Hand gesetzt, die Automatik ändert ihn nicht"
      />
      <Item
        swatch={<b className="text-ink-soft tabular-nums">98 €</b>}
        label="gedämpft: Standardpreis des Apartments"
      />
      <Item
        swatch={
          <span className="border-navy/30 grid size-4 place-items-center rounded-[5px] border">
            <OtaLogo channel="BookingCom" size={10} />
          </span>
        }
        label="Kanal per Regel geschlossen, Preis bleibt"
      />
      {closedByFamily && (
        <Item swatch={<Link2 size={13} aria-hidden />} label={FAMILY_CLOSED_LABEL} />
      )}
      {doubleBooked && (
        <Item swatch={<DoubleBookedMark className="w-6" />} label={DOUBLE_BOOKED_LABEL} />
      )}
      <Item
        swatch={<span className="border-forest bg-forest-soft h-4 w-6 rounded border-2" />}
        label="Auswahl"
      />
      {automation && (
        <>
          <Item swatch={<ModeChip mode="Automatic" />} label="Modus der Preisautomatik" />
          <Item
            swatch={<b className="text-amber-ink tabular-nums">104 → 118</b>}
            label="Ziel der Automatik, noch nicht bestätigt"
          />
          <Item swatch={<Mark failed={false} />} label="gesendet, Bestätigung offen" />
          <Item swatch={<Mark failed />} label="Übertragung fehlgeschlagen" />
        </>
      )}
      <span>2 N. = Mindestnächte</span>
    </div>
  );
}

function Mark({ failed }: { failed: boolean }) {
  return (
    <span
      className={
        failed
          ? 'bg-sealwax-tint text-sealwax grid size-[15px] place-items-center rounded-full text-[9px] font-extrabold'
          : 'bg-amber-tint text-amber-ink grid size-[15px] place-items-center rounded-full text-[9px] font-extrabold'
      }
    >
      {failed ? '!' : '↗'}
    </span>
  );
}

function Item({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {swatch}
      {label}
    </span>
  );
}
